import { useEffect } from 'react';
import { useLanguageStore } from '../store';

const SITE_NAME = 'FEDYK Furniture';
const DOMAIN = 'https://fedykfurniture.com';
const LANGS = ['en', 'nl', 'ro', 'ru'];
// Региональные языковые стандарты (Бельгия-NL, Румыния-RO, международный EN, RU).
const HREFLANG = { en: 'en', nl: 'nl-BE', ro: 'ro-RO', ru: 'ru' };

const defaultTitles = {
  en: 'FEDYK Furniture — Premium Interior Solutions',
  nl: 'FEDYK Furniture — Premium Interieuroplossingen',
  ro: 'FEDYK Furniture — Solutii Premium de Interior',
  ru: 'FEDYK Furniture — Премиальные интерьерные решения',
};

const defaultDescriptions = {
  en: 'Premium kitchens, wardrobes, doors, wall panels and custom furniture. Architectural elegance crafted in Europe.',
  nl: 'Premium keukens, kasten, deuren, wandpanelen en maatwerk meubels. Architecturale elegantie vervaardigd in Europa.',
  ro: 'Bucatarii, garderobe, usi, panouri de perete si mobilier la comanda premium. Eleganta arhitecturala fabricata in Europa.',
  ru: 'Премиальные кухни, шкафы, двери, стеновые панели и мебель на заказ. Архитектурная элегантность, созданная в Европе.',
};

const buildOrgSchema = (origin, lang) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: origin || DOMAIN,
  ...(origin ? { logo: `${origin}/logo512.png` } : {}),
  description: defaultDescriptions[lang] || defaultDescriptions.en,
  brand: { '@type': 'Brand', name: SITE_NAME },
});

export const SEO = ({ title, description, path, image, jsonLd, noindex = false }) => {
  const { language } = useLanguageStore();
  const lang = language || 'en';
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const origin = isLocalhost ? window.location.origin : DOMAIN;

    // 1. Извлекаем и очищаем путь
    let rawPathname = path 
      ? (path.startsWith('/') ? path : `/${path}`)
      : window.location.pathname;

    // Удаляем завершающий слэш (кроме самого корня)
    if (rawPathname.length > 1 && rawPathname.endsWith('/')) {
      rawPathname = rawPathname.slice(0, -1);
    }

    const segs = rawPathname.split('/');
    const hasLangPrefix = LANGS.includes(segs[1]);
    const isAdmin = segs[1] === 'admin' || rawPathname.startsWith('/admin');

    // 2. СТРОГОЕ ФОРМИРОВАНИЕ CANONICAL PATH
    let canonicalPath = rawPathname;
    if (!isAdmin) {
      if (!hasLangPrefix) {
        // Если путь '/' или '/about', принудительно подставляем текущий язык страницы
        canonicalPath = rawPathname === '/' ? `/${lang}` : `/${lang}${rawPathname}`;
      }
    }

    const fullTitle = (title && typeof title === 'string' && title.trim().length > 0)
      ? `${title} | ${SITE_NAME}`
      : (defaultTitles[lang] || defaultTitles.en);
      
    const fullDescription = (description && typeof description === 'string' && description.trim().length > 0)
      ? description
      : (defaultDescriptions[lang] || defaultDescriptions.en);

    document.title = fullTitle;
    document.documentElement.lang = lang;

    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(name.startsWith('og:') || name.startsWith('twitter:') ? 'property' : 'name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', fullDescription);
    setMeta('og:title', fullTitle);
    setMeta('og:description', fullDescription);
    setMeta('og:type', 'website');
    setMeta('og:site_name', SITE_NAME);
    setMeta('og:locale', (HREFLANG[lang] || 'en').replace('-', '_'));
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', fullDescription);
    if (image) { 
      setMeta('og:image', image); 
      setMeta('twitter:image', image); 
    }
    setMeta('robots', (noindex || isAdmin) ? 'noindex, nofollow' : 'index, follow');

    // Очищаем старые теги
    document.querySelectorAll('link[data-seo="alt"], meta[data-seo="alt"]').forEach((el) => el.remove());
    
    const canonicalUrl = `${origin}${canonicalPath}`;
    setMeta('og:url', canonicalUrl);

    const addLink = (rel, href, hreflang) => {
      const el = document.createElement('link');
      el.setAttribute('rel', rel);
      el.setAttribute('href', href);
      if (hreflang) el.setAttribute('hreflang', hreflang);
      el.setAttribute('data-seo', 'alt');
      document.head.appendChild(el);
    };

    // Добавляем точный Canonical URL
    addLink('canonical', canonicalUrl);

    // 3. ФОРМИРОВАНИЕ HREFLANG АЛЬТЕРНАТИВ
    if (!isAdmin) {
      // Выделяем остаток пути без языкового префикса
      const rest = hasLangPrefix ? segs.slice(2).join('/') : segs.slice(1).join('/');
      const suffix = rest ? `/${rest}` : '';

      // Генерируем alternate ссылки для всех 4 языков
      LANGS.forEach((L) => addLink('alternate', `${origin}/${L}${suffix}`, HREFLANG[L]));
      
      // x-default ведет на английскую версию той же страницы
      addLink('alternate', `${origin}/en${suffix}`, 'x-default');

      // Мета-теги альтернативных локалей для соцсетей
      LANGS.filter((L) => L !== lang).forEach((L) => {
        const m = document.createElement('meta');
        m.setAttribute('property', 'og:locale:alternate');
        m.setAttribute('content', (HREFLANG[L] || L).replace('-', '_'));
        m.setAttribute('data-seo', 'alt');
        document.head.appendChild(m);
      });
    }

    // 4. JSON-LD СХЕМА
    document.querySelectorAll('script[data-seo="jsonld"]').forEach((el) => el.remove());
    if (!isAdmin) {
      const blocks = [buildOrgSchema(origin, lang)];
      if (jsonLd) {
        if (Array.isArray(jsonLd)) blocks.push(...jsonLd.filter(Boolean));
        else blocks.push(jsonLd);
      }
      blocks.forEach((b) => {
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.setAttribute('data-seo', 'jsonld');
        s.text = JSON.stringify(b);
        document.head.appendChild(s);
      });
    }
  }, [title, description, lang, path, image, jsonLdKey, noindex]);

  return null;
};

export default SEO;
